import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Boxes,
  ChevronRight,
  CreditCard,
  Compass,
  Languages,
  LayoutGrid,
  Library,
  LogOut,
  Monitor,
  Moon,
  Palette,
  Settings,
  Sun,
} from "lucide-react";
import HeaderAppSearch from "./HeaderAppSearch";
import { useScrollAwareHeader } from "./useScrollAwareHeader";
import "./scroll-aware-header.css";
import { useThemePreference, type ThemePreference } from "@/theme/theme";

const THEME_CHOICES: ReadonlyArray<{ value: ThemePreference; label: string; Icon: typeof Sun }> = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];
import { useDiscoveryPanels } from "./DiscoveryPanels";
import type { SearchContextValue } from "@/components/search/SearchProvider";
import type { AccountUser } from "@/auth/accountTypes";
import { isDesktop } from "@/desktop/bridge";
import { isRouteAvailable } from "@/config/featureFlags";
import { LANGUAGES } from "@/i18n/languages";
import AtoLogo from "./AtoLogo";
import LanguageSelect from "@/i18n/LanguageSelect";

type GalleryHeaderProps = {
  onNavigate: (route: string, opts?: { launchIntent?: { target: string } }) => void;
  user?: AccountUser | null;
  onSignIn?: () => void;
  onSignOut?: () => void;
  activeSection?: "discover" | "library" | "studio" | null;
  scrollHeaderScreen?: "discover" | "library" | null;
  search?: Pick<
    SearchContextValue,
    "closePalette"
  >;
};

type AccountSubmenu = "appearance" | "language" | null;

export default function GalleryHeader({
  onNavigate,
  user,
  onSignIn,
  onSignOut,
  search,
  activeSection,
  scrollHeaderScreen,
}: GalleryHeaderProps) {
  const headerRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const desktop = isDesktop();
  const panels = useDiscoveryPanels();
  const scrollScreen = (scrollHeaderScreen === "discover" && panels?.workspaceView !== "list") ||
    (scrollHeaderScreen === "library" && !panels?.libraryActive) ? null : scrollHeaderScreen;
  const hidden = useScrollAwareHeader(Boolean(scrollScreen), scrollScreen ?? "", headerRef);
  function browse(panel: "discover" | "library") {
    search?.closePalette();
    if (panel === "library") {
      panels?.setPanel(null);
      onNavigate("/?view=my-apps");
      return;
    }
    if (panels?.workspaceActive) {
      // Discover always means the Grid, on every width; Preview is a state
      // inside it. Without a mounted Grid, phones fall back to the sheet.
      panels.setPanel(null);
      if (panels.showGrid()) return;
      if (window.innerWidth < 900) panels.setPanel(panel);
      else document.getElementById("discovery-discover-panel")?.focus();
      return;
    }
    onNavigate("/?view=discover");
  }
  // Enter in the header search: results in Discover's Grid. A running app in
  // Preview keeps its selection (and so its session) behind the Grid.
  function searchApps(query: string) {
    const here = new URL(window.location.href);
    const params = here.pathname === "/" && here.searchParams.get("view") === "discover"
      ? here.searchParams
      : new URLSearchParams({ view: "discover" });
    // Results are a Grid; a Preview selection never rides along.
    params.delete("c");
    params.delete("app");
    params.delete("style");
    params.set("view", "discover");
    params.delete("theme");
    if (query) params.set("q", query);
    else params.delete("q");
    onNavigate(`/?${params.toString()}`);
  }
  const [theme, setTheme] = useThemePreference();
  // The avatar menu is identity + account destinations + two setting
  // submenus. Creation and publications live in Studio, reached from the
  // header's Studio entry, so they are not repeated here.
  const [menuOpen, setMenuOpen] = useState(false);
  const [submenu, setSubmenu] = useState<AccountSubmenu>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) {
      setSubmenu(null);
      return;
    }
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Escape first backs out of a submenu, then closes the menu.
        if (submenu) setSubmenu(null);
        else setMenuOpen(false);
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen, submenu]);

  const currentTheme = THEME_CHOICES.find((choice) => choice.value === theme);
  const currentLanguage =
    LANGUAGES.find((language) => language.code === locale)?.name ?? locale;

  return (
    <header
      ref={headerRef}
      data-scroll-screen={scrollScreen || undefined}
      className={`gallery-header${search ? " gallery-header--discovery" : ""}${scrollScreen ? " gallery-header--scroll-aware" : ""}${scrollScreen && hidden ? " gallery-header--scroll-hidden" : ""}`}
    >
      <button
        type="button"
        className="gallery-header-brand"
        onClick={() => onNavigate("/")}
        aria-label={t("Home")}
        title={t("Home")}
      >
        <AtoLogo size={24} />
      </button>

      {search ? (
        <>
          <nav className="discovery-header-nav" aria-label={t("Primary")}>
            <button
              type="button"
              aria-label={t("Discover")}
              title={t("Discover")}
              aria-current={
                (panels?.panel ?? activeSection) === "discover"
                  ? "page"
                  : undefined
              }
              onClick={() => browse("discover")}
              aria-haspopup={panels?.workspaceActive ? "dialog" : undefined}
            >
              <Compass size={18} aria-hidden="true" /> <span className="discovery-nav-desktop-label"><Message message={"Discover"} /></span><span className="discovery-nav-mobile-label"><Message message={"Explore apps"} /></span>
            </button>
            <button
              type="button"
              aria-label={t("Library")}
              title={t("Library")}
              aria-current={
                activeSection === "library"
                  ? "page"
                  : undefined
              }
              onClick={() => browse("library")}
            >
              <Library size={18} aria-hidden="true" /> <span><Message message={"Library"} /></span>
            </button>
          </nav>
        </>
      ) : null}
      <div className="gallery-header-actions">
        {search ? (
          <>
            <HeaderAppSearch onSearch={searchApps} onOpenApp={(route, target) => onNavigate(route, { launchIntent: { target } })} />
            <button
              type="button"
              className={`header-publish-button${activeSection === "studio" ? " is-active" : ""}`}
              title={t("Studio")}
              aria-label={t("Studio")}
              aria-current={activeSection === "studio" ? "page" : undefined}
              onClick={() => onNavigate("/studio")}
            >
              <LayoutGrid size={18} aria-hidden="true" /><span><Message message="Studio" /></span>
            </button>
          </>
        ) : null}
        {user ? (
          <div className="run-overflow gallery-avatar-menu" ref={menuRef}>
            <button
              type="button"
              className="gallery-avatar-btn"
              aria-label={t("Account menu")}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              {user.image ? (
                <img src={user.image} alt="" />
              ) : (
                (user.name || user.email || "?").charAt(0).toUpperCase()
              )}
            </button>
            {menuOpen ? (
              <div className="run-overflow-menu gallery-account-menu" role="menu">
                {submenu === null ? (
                  <>
                    {/* Who you are signed in as, before anything you can do
                        about it — the menu is otherwise unlabelled verbs. */}
                    <div className="account-menu-identity">
                      <span className="account-menu-identity-avatar" aria-hidden="true">
                        {user.image ? <img src={user.image} alt="" /> : (user.name || user.email || "?").charAt(0).toUpperCase()}
                      </span>
                      <span className="account-menu-identity-text">
                        <span className="account-menu-identity-name">
                          {user.name || user.email || t("Signed in")}
                        </span>
                        {user.email && user.name ? (
                          <span className="account-menu-identity-email">
                            {user.email}
                          </span>
                        ) : null}
                      </span>
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      className="run-overflow-item account-menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        onNavigate("/settings/billing/plans");
                      }}
                    >
                      <CreditCard size={19} aria-hidden="true" />
                      <span className="account-menu-item-label"><Message message={"Plan & billing"} /></span>
                      <ChevronRight size={16} className="account-menu-chevron" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="run-overflow-item account-menu-item"
                      onClick={() => {
                        setMenuOpen(false);
                        onNavigate("/settings");
                      }}
                    >
                      <Settings size={19} aria-hidden="true" />
                      <span className="account-menu-item-label"><Message message={"Settings"} /></span>
                      <ChevronRight size={16} className="account-menu-chevron" aria-hidden="true" />
                    </button>
                    {isRouteAvailable("/runners", { desktop }) ? (
                      <button
                        type="button"
                        role="menuitem"
                        className="run-overflow-item account-menu-item"
                        onClick={() => {
                          setMenuOpen(false);
                          onNavigate("/runners");
                        }}
                      >
                        <Boxes size={19} aria-hidden="true" />
                        <span className="account-menu-item-label"><Message message={"Runners"} /></span>
                      </button>
                    ) : null}
                    {/* Both settings open in place: the menu stays open, the
                        current value stays visible on the row. */}
                    <button
                      type="button"
                      role="menuitem"
                      className="run-overflow-item account-menu-item"
                      aria-haspopup="menu"
                      onClick={() => setSubmenu("appearance")}
                    >
                      <Palette size={19} aria-hidden="true" />
                      <span className="account-menu-item-label"><Message message={"Appearance"} /></span>
                      <span className="account-menu-value">{t(currentTheme?.label ?? "System")}</span>
                      <ChevronRight size={16} className="account-menu-chevron" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="run-overflow-item account-menu-item"
                      aria-haspopup="menu"
                      onClick={() => setSubmenu("language")}
                    >
                      <Languages size={19} aria-hidden="true" />
                      <span className="account-menu-item-label"><Message message={"Language"} /></span>
                      <span className="account-menu-value">{currentLanguage}</span>
                      <ChevronRight size={16} className="account-menu-chevron" aria-hidden="true" />
                    </button>
                    <div className="account-menu-separator" role="separator" />
                    <button
                      type="button"
                      role="menuitem"
                      className="run-overflow-item account-menu-item account-menu-item--danger"
                      onClick={() => {
                        setMenuOpen(false);
                        onSignOut?.();
                      }}
                    >
                      <LogOut size={19} aria-hidden="true" />
                      <span className="account-menu-item-label"><Message message={"Sign out"} /></span>
                    </button>
                  </>
                ) : submenu === "appearance" ? (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="run-overflow-item account-menu-item account-menu-back"
                      onClick={() => setSubmenu(null)}
                    >
                      <ArrowLeft size={19} aria-hidden="true" />
                      <span className="account-menu-item-label"><Message message={"Back"} /></span>
                    </button>
                    <div className="account-menu-separator" role="separator" />
                    <div className="account-menu-submenu" role="group" aria-label={t("Appearance")}>
                      {THEME_CHOICES.map(({ value, label, Icon }) => (
                        <button
                          key={value}
                          type="button"
                          role="menuitemradio"
                          aria-checked={theme === value}
                          className="run-overflow-item account-menu-item account-menu-theme"
                          onClick={() => setTheme(value)}
                        >
                          <Icon size={19} aria-hidden="true" />
                          <span className="account-menu-item-label">{t(label)}</span>
                          <span className="account-menu-theme-radio" aria-hidden="true" />
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="run-overflow-item account-menu-item account-menu-back"
                      onClick={() => setSubmenu(null)}
                    >
                      <ArrowLeft size={19} aria-hidden="true" />
                      <span className="account-menu-item-label"><Message message={"Back"} /></span>
                    </button>
                    <div className="account-menu-separator" role="separator" />
                    <div className="account-menu-language account-menu-submenu" role="group" aria-label={t("Language")}>
                      <LanguageSelect compact />
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            className="gallery-signin-btn ato-button ato-button--secondary"
            onClick={() => (onSignIn ? onSignIn() : onNavigate("/settings"))}
          ><Message message={"Sign in"} /></button>
        )}
      </div>
    </header>
  );
}
